<script setup>
import { computed, ref } from 'vue';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { useMessageContext } from '../provider.js';
import { ATTACHMENT_TYPES } from '../constants';
import BaseBubble from './Base.vue';
import Icon from 'next/icon/Icon.vue';

import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';

const { attachments, sender } = useMessageContext();

const $store = useStore();
const { t } = useI18n();
const savingIndex = ref(null);

const fileTypeOf = attachment =>
  attachment?.fileType ?? attachment?.file_type ?? ATTACHMENT_TYPES.CONTACT;

const formatPhone = rawDigits => {
  if (!rawDigits) return '';
  const parsed = parsePhoneNumberFromString(`+${rawDigits}`);
  if (!parsed?.isValid()) return `+${rawDigits}`;
  return parsed.country === 'BR'
    ? `+55 ${parsed.formatNational()}`
    : parsed.formatInternational();
};

// Cor fixa por nome para o avatar (texto branco com contraste AA em todas).
const AVATAR_COLORS = [
  '#2563eb',
  '#7c3aed',
  '#c026d3',
  '#db2777',
  '#0d9488',
  '#15803d',
  '#c2410c',
  '#0369a1',
];
const avatarColorOf = seed => {
  const hash = [...String(seed || '')].reduce(
    (total, char) => (total * 31 + char.charCodeAt(0)) % 9973,
    7
  );
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

const initialsOf = name =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0].toUpperCase())
    .join('');

// Uma mensagem do WhatsApp pode trazer vários contatos: cada anexo vira uma linha.
const contacts = computed(() =>
  (attachments.value || [])
    .filter(attachment => fileTypeOf(attachment) === ATTACHMENT_TYPES.CONTACT)
    .map(attachment => {
      const meta = attachment.meta || {};
      const name = [
        meta.firstName || meta.first_name,
        meta.lastName || meta.last_name,
      ]
        .filter(Boolean)
        .join(' ')
        .trim();
      const rawPhone = String(
        attachment.fallbackTitle || attachment.fallback_title || ''
      ).replace(/\D/g, '');
      return {
        name,
        initials: initialsOf(name),
        color: avatarColorOf(name || rawPhone),
        rawPhone,
        phone: formatPhone(rawPhone),
      };
    })
);

const senderName = computed(() => sender?.value?.name || '');
const heading = computed(() => {
  const title =
    contacts.value.length > 1
      ? t('CONVERSATION.SHARED_CONTACT.TITLE_MANY', {
          count: contacts.value.length,
        })
      : t('CONVERSATION.SHARED_CONTACT.TITLE_ONE');
  return senderName.value
    ? t('CONVERSATION.SHARED_CONTACT.SHARED_BY', {
        title,
        sender: senderName.value,
      })
    : title;
});

async function filterContactByNumber(searchCandidate) {
  const query = {
    attribute_key: 'phone_number',
    filter_operator: 'equal_to',
    values: [`+${searchCandidate}`, searchCandidate],
    attribute_model: 'standard',
    custom_attribute_type: '',
  };

  const queryPayload = { payload: [query] };
  const found = await $store.dispatch('contacts/filter', {
    queryPayload,
    resetState: false,
  });
  return found.shift();
}

function openContactNewTab(contactId) {
  const accountId = window.location.pathname.split('/')[3];
  const url = `/app/accounts/${accountId}/contacts/${contactId}`;
  window.open(url, '_blank', 'noopener');
}

async function saveContact(contact, index) {
  if (savingIndex.value !== null) return;
  savingIndex.value = index;
  try {
    let saved = await filterContactByNumber(contact.rawPhone);
    if (!saved) {
      saved = await $store.dispatch('contacts/create', {
        name: contact.name,
        phone_number: `+${contact.rawPhone}`,
      });
      useAlert(t('CONTACT_FORM.SUCCESS_MESSAGE'));
    }
    openContactNewTab(saved.id);
  } catch (error) {
    if (error instanceof DuplicateContactException) {
      if (error.data.includes('phone_number')) {
        useAlert(t('CONTACT_FORM.FORM.PHONE_NUMBER.DUPLICATE'));
      }
    } else if (error instanceof ExceptionWithMessage) {
      useAlert(error.data);
    } else {
      useAlert(t('CONTACT_FORM.ERROR_MESSAGE'));
    }
  } finally {
    savingIndex.value = null;
  }
}

async function copyPhone(contact) {
  try {
    await copyTextToClipboard(`+${contact.rawPhone}`);
    useAlert(t('CONVERSATION.SHARED_CONTACT.COPIED'));
  } catch {
    useAlert(t('CONVERSATION.SHARED_CONTACT.COPY_FAILED'));
  }
}
</script>

<template>
  <BaseBubble
    class="w-72 !max-w-full p-2"
    meta-padding-class="px-1 pt-1"
    data-bubble-name="contact"
  >
    <div class="grid gap-2">
      <p
        class="mb-0 flex items-center gap-1.5 px-1 text-xs text-current opacity-75"
      >
        <Icon icon="i-hugeicons-contact-01" class="size-3.5 shrink-0" />
        <span class="truncate">{{ heading }}</span>
      </p>

      <div
        v-for="(contact, index) in contacts"
        :key="`${contact.rawPhone}-${index}`"
        class="overflow-hidden rounded-lg bg-[rgb(0_0_0/0.06)] dark:bg-white/10"
        data-test="shared-contact"
      >
        <div class="flex min-w-0 items-center gap-3 p-2.5">
          <span
            class="grid size-10 shrink-0 place-content-center rounded-full text-sm font-semibold text-white"
            :style="{ backgroundColor: contact.color }"
            aria-hidden="true"
          >
            <template v-if="contact.initials">{{ contact.initials }}</template>
            <Icon v-else icon="i-hugeicons-user" class="size-5" />
          </span>
          <div class="min-w-0 flex-1">
            <p
              class="mb-0 truncate text-sm font-semibold text-current"
              :title="contact.name"
            >
              {{ contact.name || t('CONVERSATION.SHARED_CONTACT.NO_NAME') }}
            </p>
            <p
              class="mb-0 mt-0.5 truncate text-xs tabular-nums text-current opacity-80"
            >
              {{ contact.phone || t('CONVERSATION.SHARED_CONTACT.NO_PHONE') }}
            </p>
          </div>
          <button
            v-if="contact.rawPhone"
            v-tooltip="t('CONVERSATION.SHARED_CONTACT.COPY')"
            type="button"
            class="grid size-8 shrink-0 place-content-center rounded-full text-current opacity-80 hover:bg-[rgb(0_0_0/0.08)] hover:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-current dark:hover:bg-white/10"
            :aria-label="t('CONVERSATION.SHARED_CONTACT.COPY')"
            data-test="copy-shared-contact"
            @click="copyPhone(contact)"
          >
            <Icon icon="i-hugeicons-copy-01" class="size-4" />
          </button>
        </div>
        <button
          v-if="contact.rawPhone"
          type="button"
          class="flex h-9 w-full items-center justify-center gap-1.5 border-0 border-t border-solid border-[rgb(0_0_0/0.1)] text-sm font-medium text-current hover:bg-[rgb(0_0_0/0.04)] focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-current disabled:cursor-wait disabled:opacity-70 dark:border-white/10 dark:hover:bg-white/5"
          :disabled="savingIndex !== null"
          data-test="save-shared-contact"
          @click="saveContact(contact, index)"
        >
          <Icon
            :icon="
              savingIndex === index
                ? 'i-hugeicons-loading-03'
                : 'i-hugeicons-user-add-01'
            "
            class="size-4"
            :class="{ 'animate-spin': savingIndex === index }"
          />
          {{
            savingIndex === index
              ? t('CONVERSATION.SHARED_CONTACT.SAVING')
              : t('CONVERSATION.SAVE_CONTACT')
          }}
        </button>
      </div>
    </div>
  </BaseBubble>
</template>
